
/*!
 * Copyright (C) 2022-2025, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2022-07-05
 *			- Refactor hotel room page link item related functionality from filter-widget.js to <page-list-room> component.
 *
 *		2023-03-31 (jhoward)
 *			- Refactor <page-link-room> to use TypeScript.
 *      - <page-link-room> calculates visibility based on current filters.
 *
 *		2024-06-10 (jhoward)
 *			- Add aria-role attribute to <page-link-room> container.
 *
 *		2024-06-10 (jhoward)
 *			- Enable adding resort fee to room rate in <page-link-room>.
 *
 *		2025-01-24 (jhoward)
 *			- Add more dynamic bed type and count display in <page-link-room>.
 *			- Search all bed type variants when filtering in <page-link-room>.
*/

import {defineComponent, PropType} from "vue";
import HotelRoomCallToAction from "./hotelRoomCallToAction.vue";
import HotelRoomIcons from "./hotelRoomIcons.vue";
import {HotelPage, HotelRoomData, PageListRoomSearchData, RoomAvailabilityData, RoomAvailabilityRoomType} from "./defs";

export default defineComponent({
  name: 'pageListRoom',
  components: {
    HotelRoomCallToAction,
    HotelRoomIcons,
  },
  props: {
    offerCodes: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    searchData: {
      type: Object as PropType<PageListRoomSearchData>,
      default: function () {
        return {
          'SQ-FT-LOW': 0,
          'SQ-FT-HIGH': 0,
          'BED': '',
          'BATH-COUNT': 0
        };
      }
    },
    page: {
      type: Object as PropType<HotelPage>,
      default: function () {
        return {
          ID: 0,
          TITLE: '',
          URL: '',
          IMG_SRC: '',
          IMG_FOCUS_X: 50,
          IMG_FOCUS_Y: 50
        };
      }
    },
    roomData: {
      type: Array as PropType<HotelRoomData[]>,
      default: function () {
        return [{
          ROOM_TITLE: '',
          ROOM_TITLE_FIXED: '',
          ROOM_SQ_FT: 0,
          ROOM_BED_TYPE: '',
          ROOM_BED_COUNT: 0,
          ROOM_BATH_COUNT: 0,
          ROOM_SMOKING: false,
          ROOM_NON_SMOKING: false,
          ROOM_ADA: false,
          BOOKING_LINK: '#'
        }];
      }
    }
  },
  watch: {
    isRoomHiddenByFilters(): void {
      if (this.isRoomHiddenByFilters) {
        setTimeout(() => {
          $(this.$el).data('scrolled', false);
          (this.$el as HTMLElement).dataset.scrolled = 'false';
        }, 350);
      }
    },
  },
  computed: {
    isRoomHiddenByFilters(): boolean {
      for (const filterType in this.$store.getters.selectedFilters) {
        if (this.$store.getters.selectedFilters[filterType].length) {
          //@ts-ignore
          if (this[this.getFilterMethodName(filterType)](this.$store.getters.selectedFilters[filterType])) {
            return true;
          }
        }
      }

      return false;
    },

    icons(): {[key: string]: boolean} {
      let output: {[key: string]: boolean} = {
        'non-smoking': false,
        smoking: false,
        ada: false
      };
      this.roomData.forEach(roomTypeData => {
        if (roomTypeData.hasOwnProperty('ROOM_SMOKING') && roomTypeData.ROOM_SMOKING) {
          output.smoking = true;
        }
        if (roomTypeData.hasOwnProperty('ROOM_NON_SMOKING') && roomTypeData.ROOM_NON_SMOKING) {
          output['non-smoking'] = true;
        }
        if (roomTypeData.hasOwnProperty('ROOM_ADA') && roomTypeData.ROOM_ADA) {
          output.ada = true;
        }
      });

      return output;
    },

    iconsAvailable(): {[key: string]: boolean} {
      let output: {[key: string]: boolean} = {
        anyRoomType: this.isAvailable,
        'non-smoking': false,
        smoking: false,
        ada: false
      };
      this.roomAvailabilityData.ROOM_TYPES.forEach((roomType: RoomAvailabilityRoomType) => {
        if (roomType.hasOwnProperty('ROOM_SMOKING') && roomType.ROOM_SMOKING) {
          output.smoking = true;
        }
        if (roomType.hasOwnProperty('ROOM_NON_SMOKING') && roomType.ROOM_NON_SMOKING) {
          output['non-smoking'] = true;
        }
        if (roomType.hasOwnProperty('ROOM_ADA') && roomType.ROOM_ADA) {
          output.ada = true;
        }
      });

      return output;
    },

    isAvailable(): boolean {
      return this.$store.getters.roomTypeIsAvailable(this.roomAvailabilityData);
    },
    isSmoking(): boolean {
      for (let i = 0, l = this.roomData.length; i < l; i++) {
        if (this.roomData[i].ROOM_SMOKING) {
          return true;
        }
      }

      return false;
    },
    isNonSmoking(): boolean {
      for (let i = 0, l = this.roomData.length; i < l; i++) {
        if (this.roomData[i].ROOM_NON_SMOKING) {
          return true;
        }
      }

      return false;
    },

    containerClasses(): {[key: string]: boolean} {
      let classes: {[key: string]: boolean} = {
        hide: this.isRoomHiddenByFilters,
        scrolled: $(this.$el).data('scrolled')
      };
      const iconCount = Object.entries(this.icons).filter(icon => {
        return icon[1];
      }).length;
      classes['icon-count-' + iconCount] = true;

      return classes;
    },

    imageStyles(): {[key: string]: string} {
      return {
        'background-position': this.page.IMG_FOCUS_X + '% ' + this.page.IMG_FOCUS_Y + '%',
        'background-image': 'url(\'' + this.page.IMG_SRC + '\')'
      };
    },

    roomAvailabilityData(): RoomAvailabilityData {
      if (this.page.ID > 0) {
        for (let i = 0, l = this.$store.state.ROOMS.length; i < l; i++) {
          if (this.$store.state.ROOMS[i].PAGE_ID === this.page.ID) {
            return this.$store.state.ROOMS[i];
          }
        }
      }
      return {
        ADA_VARIANTS: [],
        DATA_AVAILABLE: false,
        PAGE_ID: 0,
        ROOM_TYPES: []
      };
    },

    averageNightlyPrice(): number {
      if (this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].ROOM_AVERAGE_PRICE || 0;
      }

      return 0;
    },

    roomSqFt(): string {
      if (this.searchData['SQ-FT-HIGH'] !== this.searchData['SQ-FT-LOW']) {
        return this.searchData['SQ-FT-LOW'] + '-' + this.searchData['SQ-FT-HIGH'];
      }

      return '' + this.searchData['SQ-FT-HIGH'];
    },
    bedTypes(): {[key: string]: number[]} {
      const output: {[key: string]: number[]} = {};
      this.roomData.forEach(roomTypeData => {
        if (roomTypeData.ROOM_BED_TYPE.length && roomTypeData.ROOM_BED_COUNT > 0) {
          if (!output.hasOwnProperty(roomTypeData.ROOM_BED_TYPE)) {
            output[roomTypeData.ROOM_BED_TYPE] = [];
          }
          if (-1 === output[roomTypeData.ROOM_BED_TYPE].indexOf(roomTypeData.ROOM_BED_COUNT)) {
            output[roomTypeData.ROOM_BED_TYPE].push(roomTypeData.ROOM_BED_COUNT);
          }
        }
      });

      return output;
    },
    bedCount(): string {
      const output: string[] = [];
      for (const bedType in this.bedTypes) {
        output.push(this.bedTypes[bedType].join(' or '));
      }

      return output.join(' / ');
      // return this.searchData['BED'].split('-')[0];
    },
    bedType(): string {
      const output: string[] = [];
      for (const bedType in this.bedTypes) {
        output.push(bedType);
      }

      return output.join(' / ');
      // return this.searchData['BED'].split('-')[1] || '';
    },
  },
  methods: {
    isRoomHiddenByAccessibilityFilter(chosenAccessibilityTypes: string[]): boolean {
      if (0 === chosenAccessibilityTypes.length) {
        return false;
      }

      for (let i = 0, l = this.roomAvailabilityData.ADA_VARIANTS.length; i < l; i++) {
        if (-1 !== chosenAccessibilityTypes.indexOf(this.roomAvailabilityData.ADA_VARIANTS[i].toLowerCase())) {
          return false;
        }
      }
      return true;
    },
    isRoomHiddenByBedTypeFilter(chosenBedTypes: string[]): boolean {
      for (let bedType in this.bedTypes) {
        for (let i = 0; i < this.bedTypes[bedType].length; i++) {
          if (-1 !== chosenBedTypes.indexOf(this.bedTypes[bedType][i] + '-' + bedType.toUpperCase())) {
            return false;

          }
        }
      }
      return true;
    },
    isRoomHiddenByPriceFilter(chosenPriceRanges: string[]): boolean {
      if (!chosenPriceRanges.length || !this.searchData?.["SQ-FT-LOW"] || !this.searchData?.["SQ-FT-HIGH"]) {
        return false;
      } else if (chosenPriceRanges.length && !this.isAvailable) {
        return true;
      }

      for (let i = 0, l = chosenPriceRanges.length; i < l; i++) {
        const chosenMinMax: number[] = chosenPriceRanges[i]
            .split('-')
            .map((sqFt: string) => parseInt(sqFt, 10));
        if (1 === chosenMinMax.length) {
          chosenMinMax.push(chosenMinMax[0]);
        }
        if (this.averageNightlyPrice >= chosenMinMax[0] && this.averageNightlyPrice <= chosenMinMax[1]) {
          return false;
        }
      }

      return true;
    },
    isRoomHiddenBySmokingFilter(chosenSmokingOptions: string[]): boolean {
      if (-1 !== chosenSmokingOptions.indexOf('YES') && !this.isSmoking) {
        return true;
      }

      return -1 !== chosenSmokingOptions.indexOf('NO') && !this.isNonSmoking;
    },
    isRoomHiddenBySqFtFilter(chosenSqFtSizes: string[]): boolean {
      //sq_ft: (3) ['100-400', '400-750', '750-10000']
      if (!chosenSqFtSizes.length || !this.searchData?.["SQ-FT-LOW"] || !this.searchData?.["SQ-FT-HIGH"]) {
        return false;
      }

      for (let i = 0, l = chosenSqFtSizes.length; i < l; i++) {
        const chosenMinMax: number[] = chosenSqFtSizes[i]
            .split('-')
            .map((sqFt: string) => parseInt(sqFt, 10));
        if (1 === chosenMinMax.length) {
          chosenMinMax.push(chosenMinMax[0]);
        }
        if (
          (this.searchData["SQ-FT-LOW"] >= chosenMinMax[0] && this.searchData["SQ-FT-LOW"] <= chosenMinMax[1])
          || (this.searchData["SQ-FT-HIGH"] >= chosenMinMax[0] && this.searchData["SQ-FT-HIGH"] <= chosenMinMax[1])
        ) {
          return false;
        }
      }

      return true;
    },
    getFilterMethodName(filterType: string): string {
      const preparedFilterType = filterType.toLowerCase()
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase())
          .join('');
      return 'isRoomHiddenBy' + preparedFilterType + 'Filter';
    },
  }
})
